// src/App.tsx
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { router } from '@/app/router';
import { useAuthStore } from '@/auth/auth-store';
import { env } from '@/config/env';
import {
  DEV_AUTO_LOGIN_ENABLED,
  resolveDevAutoLoginCredentials,
} from '@/auth/dev-auto-login';
import { DEV_MANUAL_LOGIN_REQUESTED, DEV_PERSONA_LOGIN } from '@/auth/dev-persona-login';
import { ActiveSessionConflictDialog } from '@/auth/active-session-conflict-dialog';
import { AppErrorBoundary } from '@/app/app-error-boundary';
import { BrandLoading } from '@scripsteam/scrips-signal-ds';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { isQuietLoadingRoute } from '@/shared/lib/quiet-loading-route';

// Module-scoped so it survives React StrictMode's dev double-mount: the bringup
// login must fire ONCE per page load, else two concurrent GetUserDataToken calls
// race and the backend rejects the second with 409 "User already logged in".
let bringupAttempted = false;

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const login = useAuthStore((s) => s.login);
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    (async () => {
      hydrate();
      // Dev bringup: on a local `vite dev` server, auto-sign-in when `.env.local`
      // carries dev credentials, so authenticated surfaces work locally without
      // the login UX. Gated on `import.meta.env.DEV`, so it is dead-stripped from
      // every deployed build (prod + dev site show the normal login page).
      const { status } = useAuthStore.getState();
      if (DEV_MANUAL_LOGIN_REQUESTED) {
        // `?login=manual` on deployed dev: drop the stored session, show the form.
        useAuthStore.getState().logout();
      } else if (DEV_PERSONA_LOGIN && !bringupAttempted) {
        // Deployed-dev persona sign-in (`?login=<persona>`, dev-persona-login.ts).
        // Null unless this is a dev build on the dev host AND the persona is known.
        // Replaces whatever session is stored, so a tab can switch persona.
        bringupAttempted = true; // set synchronously before the await (StrictMode guard)
        // No route mark: this URL is the one the persona was asked for, not a page the old persona left open.
        useAuthStore.getState().endSessionForPersonaSwitch();
        try {
          await login(DEV_PERSONA_LOGIN.username, DEV_PERSONA_LOGIN.password, {
            recoverActiveSession: true,
            autoTakeover: true,
            keepRoute: true,
          });
        } catch (e) {
          // Fall through to the login page. Name the persona, never the credentials.
          console.warn('[devlogin] sign-in failed for persona', DEV_PERSONA_LOGIN.persona, e);
        }
      } else if (status === 'unauthenticated' && DEV_AUTO_LOGIN_ENABLED && !bringupAttempted) {
        bringupAttempted = true; // set synchronously before the await (StrictMode guard)
        // Credentials come ONLY from .env.local (no literal fallback, SEC-1). None
        // configured → fail closed: no attempt, the login page shows. login()
        // auto-takes-over a stale-session 409 in dev (UpdateDeviceDetails →
        // re-login), so a prior run doesn't block us.
        const creds = resolveDevAutoLoginCredentials(env.auth);
        if (creds) {
          try {
            await login(creds.username, creds.password, { recoverActiveSession: true });
          } catch {
            /* fall through to the login page */
          }
        }
      }
      setBooted(true);
    })();
  }, [hydrate, login]);

  // The cold-open window. `hydrate()` is synchronous in a production build so this
  // is usually one tick, but in dev an auto-login round-trip holds it open — and it
  // previously rendered NOTHING, a blank page with no sign the app was working.
  if (!booted) {
    // PROD-3194: an encounter link opened cold shows no spinner at any stage, this gate included.
    return isQuietLoadingRoute(window.location.pathname) ? (
      <QuietLoading className="h-screen" label="Starting Scrips" />
    ) : (
      <BrandLoading variant="page" label="Starting Scrips" />
    );
  }
  return (
    // Outermost error net. The router's own `errorElement`s (router.tsx) handle anything
    // thrown inside a route; this catches what sits ABOVE the router — RouterProvider's first
    // render and the session dialog — which previously white-screened with no surface at all.
    <AppErrorBoundary>
      <RouterProvider router={router} />
      {/* Deployed 409 active-session confirm dialog (dev takes over silently). */}
      <ActiveSessionConflictDialog />
    </AppErrorBoundary>
  );
}
